import { ApiClient } from '@/utils'
import { useAuthStore } from '@/stores'
import { determineAppropriateRootDomain } from '@/utils/cookie-utils'
import { getProtectedRouteRedirect } from './access-redirect'
import { getUserPlan } from '@/queries/profile/user-plan.query'
import { getRedirectDestination } from '@/utils/redirect-destination'
import { parseQuery, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'

const checkedOpeningLocations = new WeakSet<RouteLocationNormalized>()

/**
 * Cleans up tokens with .app.go2train.co domain
 * Returns true if any tokens were removed
 */
export function cleanupUnauthorizedTokens(): boolean {
  const UNAUTHORIZED_DOMAIN = '.app.go2train.co'
  const TOKEN_KEY = 'user_token'
  let tokensRemoved = false

  // Helper to remove a cookie with specific domain and all possible paths
  function removeCookieWithDomain(name: string, domain: string) {
    // Remove with different path combinations to ensure complete removal
    const paths = ['/', '/auth/sign-in', '']
    paths.forEach((path) => {
      document.cookie = `${name}=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=none`
      document.cookie = `${name}=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`
    })
  }

  try {
    // Specifically remove cookie from .app.go2train.co domain
    removeCookieWithDomain(TOKEN_KEY, UNAUTHORIZED_DOMAIN)
    tokensRemoved = true

    // Also clean local and session storage if we're on the unauthorized domain
    const currentDomain = determineAppropriateRootDomain()
    if (currentDomain?.includes('app.go2train.co')) {
      localStorage.removeItem(TOKEN_KEY)
      sessionStorage.removeItem(TOKEN_KEY)
    }

    return tokensRemoved
  } catch (error) {
    console.error('Error cleaning up app domain tokens:', error)
    return false
  }
}

// Updated authGuard implementation
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  // Clean up .app.go2train.co domain tokens before checking authorization
  const unauthorizedTokensRemoved = cleanupUnauthorizedTokens()
  if (unauthorizedTokensRemoved) {
    console.warn('App domain tokens were removed during navigation')
    // Force auth store to recheck authentication state
    authStore.restoreAuthState()
  }

  // Initialize API client authorization check
  ApiClient.checkAuthorization()

  try {
    if (authStore.isAuthenticated && (to.name === 'sign-in' || to.name === 'sign-with-password')) {
      const destination = from.matched.length && !from.meta.isAuthRoute
        ? from.fullPath
        : getRedirectDestination(to.query.redirect)
      const url = new URL(destination, window.location.origin)
      next({ path: url.pathname, query: parseQuery(url.search), hash: url.hash, replace: true })
      return
    }

    // The first navigation runs on every site opening or full-page reload,
    // including entry pages that do not require authentication.
    const isOpeningSite =
      from.matched.length === 0 && !checkedOpeningLocations.has(from)
    if (
      authStore.isAuthenticated &&
      to.name !== 'delete-account' && to.name !== 'account-deletion-result' &&
      (isOpeningSite || to.meta.requiresAuth || to.meta.requiresPlanAccess)
    ) {
      // Redirects triggered by this request still share the initial location.
      // Consume the opening check before awaiting so they cannot repeat it.
      if (isOpeningSite) checkedOpeningLocations.add(from)
      try {
        await getUserPlan()
      } catch (error) {
        // A temporary request failure must not erase cached subscription access.
        console.warn('Unable to refresh subscription access:', error)
      }
    }

    if (to.meta.requiresAuth || to.meta.requiresPlanAccess) {
      const redirect = getProtectedRouteRedirect({
        requiresAuth: Boolean(to.meta.requiresAuth),
        requiresPlanAccess: Boolean(to.meta.requiresPlanAccess),
        isAuthenticated: authStore.isAuthenticated,
        hasAccess: authStore.hasAccess,
        destination: to.fullPath
      })

      if (redirect) {
        next({
          ...redirect,
          replace: true
        })
        return
      }
    }

    // if (to.meta.isAuthRoute && authStore.isAuthenticated) {
    //   // If authenticated but no access, redirect to buy plan
    //   if (!authStore.hasAccess) {
    //     next({ name: 'buy-subscription' })
    //     return
    //   }
    //   // next({ name: 'user-subscriptions' })
    //   // return
    // }
    next()
  } catch (error) {
    console.error('Navigation guard error:', error)
    next({ name: 'sign-in' })
  }
}
